import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  listVideoGenerations,
  type VideoGenerationItem,
  type VideoGenerationPage,
} from '@/api/video-generate';
import type {
  VideoAspectRatio,
  VideoGenerationModel,
  VideoResolution,
} from '@/ai/video-models';

const PAGE_LIMIT = 20;

export interface VideoGenerationFilters {
  limit?: number;
  model?: VideoGenerationModel;
  resolution?: VideoResolution;
  aspectRatio?: VideoAspectRatio;
  status?: 'pending' | 'processing' | 'uploading' | 'completed' | 'failed';
  templateId?: string;
}

export const videoGenerationKeys = {
  all: ['videoGenerations'] as const,
  list: (filters?: VideoGenerationFilters) =>
    [...videoGenerationKeys.all, 'list', filters ?? {}] as const,
};

export function useVideoGenerations(
  filters: VideoGenerationFilters = {},
  enabled = true
) {
  const query = useInfiniteQuery({
    queryKey: videoGenerationKeys.list(filters),
    enabled,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      listVideoGenerations({
        data: {
          limit: filters.limit ?? PAGE_LIMIT,
          cursor: pageParam ?? undefined,
          model: filters.model || undefined,
          resolution: filters.resolution || undefined,
          aspectRatio: filters.aspectRatio || undefined,
          status: filters.status || undefined,
          templateId: filters.templateId || undefined,
        },
      }),
    getNextPageParam: (lastPage: VideoGenerationPage) =>
      lastPage.hasMore ? lastPage.nextCursor : undefined,
    staleTime: 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchInterval: (query) => {
      const pages = query.state.data?.pages ?? [];
      const hasActiveJob = pages.some((page) =>
        page.items?.some(
          (item) =>
            item.status === 'pending' ||
            item.status === 'processing' ||
            item.status === 'uploading'
        )
      );
      return hasActiveJob ? 3000 : false;
    },
    refetchIntervalInBackground: true,
  });

  const items = useMemo<VideoGenerationItem[]>(() => {
    if (!query.data) return [];
    return query.data.pages.flatMap((page) => page.items ?? []);
  }, [query.data]);

  return {
    items,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isRefetching: query.isRefetching,
    refetch: query.refetch,
  };
}
